'use client'

import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import type {
  Control,
  FieldErrorsImpl,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form'
import type {
  TextField,
  EmailField,
  TextAreaField,
  SelectField,
  CountryField,
  StateField,
  CheckboxField,
} from '@payloadcms/plugin-form-builder/types'

import { Error } from '@/blocks/Form/Error'
import { Checkbox as CheckboxUi } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { countryOptions } from '@/blocks/Form/Country/options'
import { stateOptions } from '@/blocks/Form/State/options'

// Map common field names (EN + DE) to valid HTML autocomplete tokens
const inferAutoComplete = (name: string): string => {
  const lower = name.toLowerCase()
  if (lower === 'name' || lower === 'fullname') return 'name'
  if (lower === 'firstname' || lower === 'vorname') return 'given-name'
  if (lower === 'lastname' || lower === 'nachname') return 'family-name'
  if (lower === 'company' || lower === 'firma' || lower === 'organization') return 'organization'
  if (lower === 'email' || lower === 'e-mail') return 'email'
  if (lower === 'phone' || lower === 'telefon' || lower === 'tel') return 'tel'
  if (lower === 'address' || lower === 'adresse' || lower === 'street' || lower === 'strasse')
    return 'street-address'
  if (lower === 'city' || lower === 'ort' || lower === 'stadt') return 'address-level2'
  if (lower === 'zip' || lower === 'plz' || lower === 'postalcode') return 'postal-code'
  if (lower === 'country' || lower === 'land') return 'country-name'
  return 'on'
}

// Custom styled input for ContactFormBlock
const ContactInput: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & {
    ref?: React.Ref<HTMLInputElement>
  }
> = ({ className, ref, ...props }) => {
  return (
    <input
      ref={ref}
      className={`w-full h-12 border border-text-primary/20 bg-background text-text-primary px-3 outline-none focus:border-text-primary ${className || ''}`}
      {...props}
    />
  )
}

// Custom styled textarea for ContactFormBlock
const ContactTextarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    ref?: React.Ref<HTMLTextAreaElement>
  }
> = ({ className, ref, ...props }) => {
  return (
    <textarea
      ref={ref}
      className={`w-full border border-text-primary/20 bg-background text-text-primary px-3 py-2 outline-none focus:border-text-primary resize-y ${className || ''}`}
      {...props}
    />
  )
}

// Custom label for ContactFormBlock
const ContactLabel: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <label
      className={`block text-base md:text-lg text-text-secondary mb-1 ${className || ''}`}
      {...props}
    >
      {children}
    </label>
  )
}

export const ContactTextField: React.FC<
  TextField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
  }
> = ({ name, defaultValue, errors, label, register, required }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactInput
        id={name}
        type="text"
        defaultValue={defaultValue}
        required={Boolean(required)}
        autoComplete={inferAutoComplete(name)}
        {...register(name, { required })}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

export const ContactEmailField: React.FC<
  EmailField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
  }
> = ({ name, defaultValue, errors, label, register, required }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactInput
        id={name}
        type="email"
        defaultValue={defaultValue}
        required={Boolean(required)}
        autoComplete="email"
        inputMode="email"
        {...register(name, { pattern: /^\S[^\s@]*@\S+$/, required })}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

export const ContactTextareaField: React.FC<
  TextAreaField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
    rows?: number
  }
> = ({ name, defaultValue, errors, label, register, required, rows = 6 }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactTextarea
        id={name}
        rows={rows}
        defaultValue={defaultValue}
        required={Boolean(required)}
        autoComplete="on"
        {...register(name, { required })}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

// Phone field (uses text input with type="tel")
export const ContactPhoneField: React.FC<
  TextField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
  }
> = ({ name, defaultValue, errors, label, register, required }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactInput
        id={name}
        type="tel"
        defaultValue={defaultValue}
        required={Boolean(required)}
        autoComplete="tel"
        inputMode="tel"
        {...register(name, { required })}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

// Number field (uses text input with type="number")
export const ContactNumberField: React.FC<
  TextField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
  }
> = ({ name, defaultValue, errors, label, register, required }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactInput
        id={name}
        type="number"
        defaultValue={defaultValue}
        required={Boolean(required)}
        inputMode="numeric"
        {...register(name, { required })}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

// Shared styled trigger so selects match ContactInput (h-12, square, brand border)
const contactSelectTriggerClass =
  'h-12 w-full rounded-none border border-text-primary/20 bg-background text-text-primary px-3 focus:ring-0 focus:ring-offset-0 focus:border-text-primary'

const ContactSelect: React.FC<{
  name: string
  label?: string | null
  control: Control
  required?: boolean | null
  defaultValue?: string
  options: { label: string; value: string }[]
}> = ({ name, label, control, required, defaultValue, options }) => {
  return (
    <Controller
      control={control}
      defaultValue={defaultValue ?? ''}
      name={name}
      rules={{ required: Boolean(required) }}
      render={({ field: { onChange, value } }) => {
        const controlledValue = options.find((t) => t.value === value)
        return (
          <Select onValueChange={onChange} value={controlledValue?.value}>
            <SelectTrigger className={contactSelectTriggerClass} id={name}>
              <SelectValue placeholder={label ?? ''} />
            </SelectTrigger>
            <SelectContent>
              {options.map(({ label: optionLabel, value: optionValue }) => (
                <SelectItem key={optionValue} value={optionValue}>
                  {optionLabel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      }}
    />
  )
}

export const ContactSelectField: React.FC<
  SelectField & {
    control: Control
    errors: Partial<FieldErrorsImpl>
  }
> = ({ name, control, errors, label, options, required, defaultValue }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactSelect
        name={name}
        label={label}
        control={control}
        required={required}
        defaultValue={defaultValue}
        options={options || []}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

export const ContactCountryField: React.FC<
  CountryField & {
    control: Control
    errors: Partial<FieldErrorsImpl>
  }
> = ({ name, control, errors, label, required }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactSelect
        name={name}
        label={label}
        control={control}
        required={required}
        options={countryOptions}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

export const ContactStateField: React.FC<
  StateField & {
    control: Control
    errors: Partial<FieldErrorsImpl>
  }
> = ({ name, control, errors, label, required }) => {
  return (
    <div>
      <ContactLabel htmlFor={name}>
        {label}
        {required && <span className="text-text-primary"> *</span>}
      </ContactLabel>
      <ContactSelect
        name={name}
        label={label}
        control={control}
        required={required}
        options={stateOptions}
      />
      {errors[name] && <Error name={name} />}
    </div>
  )
}

export const ContactCheckboxField: React.FC<
  CheckboxField & {
    errors: Partial<FieldErrorsImpl>
    register: UseFormRegister<FieldValues>
  }
> = ({ name, defaultValue, errors, label, register, required }) => {
  const props = register(name, { required: Boolean(required) })
  const { setValue } = useFormContext()
  return (
    <div>
      <div className="flex items-center gap-3">
        <CheckboxUi
          defaultChecked={defaultValue}
          id={name}
          {...props}
          onCheckedChange={(checked) => setValue(props.name, checked)}
        />
        <ContactLabel htmlFor={name} className="mb-0">
          {label}
          {required && <span className="text-text-primary"> *</span>}
        </ContactLabel>
      </div>
      {errors[name] && <Error name={name} />}
    </div>
  )
}

// Map of field types to custom components
export const contactFormFields = {
  text: ContactTextField,
  email: ContactEmailField,
  textarea: ContactTextareaField,
  phone: ContactPhoneField,
  number: ContactNumberField,
  select: ContactSelectField,
  country: ContactCountryField,
  state: ContactStateField,
  checkbox: ContactCheckboxField,
}
